<template>
  <QuantityClicker @minus="minusService"
                   @plus="plusService"
                   @amount="amountChanged"
                   :readonly="readonly"
                   :amount="amount"/>
</template>

<script setup>
import {useStore} from "vuex"
import {computed, getCurrentInstance, inject, nextTick, onBeforeMount} from "vue"
import QuantityClicker from "@/components/parts/general/QuantityClicker.vue"
import {captureScrollAnchor, restoreScrollAnchor} from "@/services/scroll-anchor.service"

const props = defineProps({
  readonly: Boolean,
  serviceId: String,
  tooth: String,
  minAmount: {
    type: Number,
    default: -1
  }
})

const store = useStore()

const versionCount = inject('versionCount')
const recordTeeth = computed(() => store.getters.recordTeethObjects)
const passedServiceQty = computed(() => recordTeeth.value.map(rt => (rt.services.filter(s => s.id === props.serviceId)).length).reduce((a, b) => a + b))
const toothObject = computed(() => recordTeeth.value.find(rt => rt.teeth === props.tooth))
const amount = computed(() => (toothObject.value?.services || []).filter(service => service.id === props.serviceId).length)
const serviceIdsWithLabServicesAttached = computed(() => store.getters.serviceIdsWithLabServicesAttached)
const visit = computed(() => store.state.patient.visit)
const insuranceCompanyIdForAutocomplete = computed(() => {
  return visit.value?.insurance_polis?.insurance_company_id || visit.value?.insurance_company_id || null
})

const instance = getCurrentInstance()

let i18n = null
let noty = null

onBeforeMount(() => {
  i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty
})

const minusService = async () => {
  if(amount.value <= props.minAmount) return
  await changeAmount('minus', 1)
}

const plusService = async () => {
  await changeAmount('plus', 1)
}

const amountChanged = (val) => {
  if(val <= props.minAmount) return
  const change = val - amount.value
  if(change === 0 || isNaN(change)) return
  changeAmount(change > 0 ? 'plus' : 'minus', Math.abs(change))
}

const changeAmount = async (sign, qty = 1) => {
  if(props.readonly) return

  if(sign === 'minus' && serviceIdsWithLabServicesAttached.value.includes(props.serviceId)) {
    if(qty >= passedServiceQty.value) {
      noty.error(i18n.t('cannot_change_services_that_already_have_laboratory_services_attached'))
      return
    }
  }

  if(toothObject.value) {
    const anchor = captureScrollAnchor(instance.proxy.$el)
    await store.dispatch('toggleServiceToToothNewVersion', {
      serviceId: props.serviceId,
      recordTooth: toothObject.value,
      scope: 'tooth',
      sign,
      qty,
      insuranceCompanyId: insuranceCompanyIdForAutocomplete.value
    })
    versionCount.value++
    await nextTick()
    restoreScrollAnchor(anchor)
  }
}
</script>
